<template>
	<div class="studentsSettings">
		<!-- Sart of alert message when saving -->
		<div class="alert"
				 :class="{'alert-success': message.status === 'success', 'alert-danger': message.status === 'danger'}"
				 v-if="!!message.message">
			{{ message.message }}
		</div>
		<!-- End of alert message when saving -->

		<!-- Start of requesting access to another student -->
		<div v-if="isRequestAccess">
			<h2>{{ $t('MAIN_CONTENT.Students.requestAccessToOterStudents') }}</h2>
			<p>
				{{ $t('MAIN_CONTENT.Students.studentsDetailOnWhatToEnter') }}
			</p>
			<div v-for="(requestStudent, index) in requestStudents"
						:key="`student-${index}`"
						:id="`student-${index}`">
				<div class="row">
					<div class="col-md-10">
						<h3>
							#{{ index + 1 }} Student
						</h3>
					</div>
					<div class="col-md-2">

						<font-awesome-icon icon="times" @click="removeStudent(index)" v-if="index" style="cursor: pointer;">
						</font-awesome-icon>
					</div>
				</div>

				<div class="row">
					<div class="col-md-4">
						<ValidationProvider name="first name" rules="required|min:2">
							<div slot-scope="{ errors }">
								<label :for="`firstName${index}`">{{ $t('MAIN_CONTENT.Students.firstNameWithColon') }}</label>
								<input class="form-control"
												:id="`firstName${index}`"
												data-vv-as="first name"
												name="firstNames"
												v-model="requestStudent.firstName" />
								<small class="text-danger">{{ errors[0] }}</small>
							</div>
						</ValidationProvider>
					</div>

					<div class="col-md-4">
						<ValidationProvider name="last name" rules="required|min:2">
							<div slot-scope="{ errors }">
								<label :for="`lastName${index}`">{{ $t('MAIN_CONTENT.Students.lastNameWithColon') }}</label>
								<input class="form-control"
												:id="`lastName${index}`"
												data-vv-as="last name"
												name="lastNames"
												v-model="requestStudent.lastName" />
								<small class="text-danger">{{ errors[0] }}</small>
							</div>
						</ValidationProvider>
					</div>

					<div class="col-md-4">
						<label :for="`birthday_${index}`">{{ $t('MAIN_CONTENT.Students.birthdayWithColon') }}</label>
						<br />
						<date-picker
							:name="`birthday_${index}`"
							v-model="requestStudent.birthday"
							:aria-label="$t('MAIN_CONTENT.Students.birthdayWithColon')"
							:describedByID="'weekOfDateInput'"
							:datePickerInputMessage="$t( 'MAIN_CONTENT.Students.birthdayDatepickerAccessibilityMessage' )"
							:label="$t('MAIN_CONTENT.Students.birthdayWithColon')"
							:locale=" getLocaleCode()"
							:validateInput="true"
							:validationName="'birthday'"
							:validationInputRules="'required|date_format:dd/mm/yyyy'"
							format="MM/DD/YYYY"
							ref="studentBirthday"
							lang="en"
							:showLabel="false"
							:clearable="false"
						/>
					</div>
				</div>

				<div class="row">
					<div class="col-md-4">
						<label :for="`grade${index}`">{{ $t('MAIN_CONTENT.commonContent.sideMenuGradeLabel') }}</label>
						<select class="form-control"
										:id="`grade${index}`"
										name="grades"
										v-model="requestStudent.grade">
							<option v-for="(grade, gradeIndex) in gradelevels"
											:value="grade.GRADE_LEVEL"
											:key="`school_${gradeIndex}`">
								{{ grade.GRADE_LEVEL_DESC }}
							</option>
						</select>
					</div>

					<div class="col-md-4">
						<label :for="`school${index}`">{{ $t('MAIN_CONTENT.Students.schoolWithColon') }}</label>
						<select class="form-control"
										:id="`school${index}`"
										name="schools"
										v-model="requestStudent.school">
							<option v-for="(school, schoolIndex) in schools"
											:value="school.SCHOOL_ID"
											:key="`school_${schoolIndex}`">
								{{ school.SCHOOL_TITLE }}
							</option>
						</select>
					</div>
				</div>

				<br />

				<template v-if="index < requestStudents.length - 1">
					<hr />
				</template>
			</div>

			<div class="row text-center">
				<button @click="addStudent" class="btn btn-info">
					<font-awesome-icon icon="plus" aria-hidden="true">
					</font-awesome-icon> {{ $t('MAIN_CONTENT.Students.addAnotherStudent') }}
				</button>
			</div>

			<hr />

			<div class="row text-center">
				<button class="btn btn-default" @click="cancel">{{ $t('commonText.cancel') }}</button>
				<button class="btn btn-primary"
								@click="requestNewStudents"
								:disabled="invaildStudentAccessRequest">
					{{ $t('MAIN_CONTENT.Students.submitRequestTitleCase') }}
				</button>
			</div>
		</div>
		<!-- End of requesting access to another student -->

		<!-- Start of Students main page -->
		<div v-else-if="!isRequestAccess && !isCreatingAccount">
			<h2>{{ $t('MAIN_CONTENT.Students.manageStudentsTitleCase') }}</h2>
			<div class="row students-list">
				<div v-for="student in students" :key="student.STUDENT_ID" class="col-xs-12 col-md-6 student-field">
					<div class="row">
						<div class="col-xs-12 col-sm-5 col-md-6 col-lg-5 photo-name">
							<img :src="student.PHOTO_URL"
									 :alt="`${student.FIRST_NAME} ${student.LAST_NAME}`" class="media-object" />
							<h3>{{student.FIRST_NAME}} {{student.LAST_NAME}}</h3>
						</div>
						<div class="col-xs-12 col-sm-7 col-md-6 student-info">
							<p v-for="enrollment in student.ENROLLMENT" :key="enrollment.SCHOOL_ID">
								<strong>{{enrollment.SCHOOL_TITLE}}</strong>
								<br />
								{{ $t('MAIN_CONTENT.commonContent.sideMenuGradeLabel') }} <strong>{{enrollment.GRADE_LEVEL}}</strong>
							</p>
							<div v-if="student.PORTAL_ACCESS_ALLOWED_FLG === 'Y' && student.HAS_ACCOUNT === 'Y'"
									class="account-status">
								<font-awesome-icon icon="check" aria-hidden="true">
								</font-awesome-icon> {{ $t('MAIN_CONTENT.Students.studentHasAnAccount') }}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="divider"></div>

			<div class="row request-access">
				<div class="col-md-12">
					<p>
						{{ $t('MAIN_CONTENT.Students.portalAccessInformationalText') }}
					</p>
					<button @click="requestAccess()" class="btn btn-primary btn-backpack">
						{{ $t('MAIN_CONTENT.Students.requestAccessToOtherStudentsTitleCase') }}
					</button>
				</div>
			</div>
		</div>
		<!-- End of Students main page -->
	</div>
</template>

<script>
	// Third party libraries
	import { mapGetters } from 'vuex';
	import { ValidationProvider, Validator  } from 'vee-validate';
	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
	import moment from 'moment';

	// API routes
	import Accounts from 'API/Accounts';
	import Schools from 'API/Schools';
	import Students from 'API/Students';
	import Gradelevels from 'API/Gradelevels';

	// Shared components in vuecommons
	import DatePicker from "COMMONS/shared/AccessabilityDatePicker.vue";

	Validator.extend('is_valid_date_object', {
		validate: (value) => {
			// Check if the value is an instance of Date and is valid
			return value instanceof Date && !isNaN(value);
		},
		getMessage: (field) => `The ${field} field must be a valid date.`,
	});

	export default {
		name: 'Students',
		data() {
			return {
				isCreatingAccount : false,
				isRequestAccess   : false,
				requestStudents:		[
					{
						firstName:	'',
						lastName:		'',
						birthday:		'',
						grade:			0,
						school:			null
					}
				],
				selectedStudent:	{},
				student: {
					email:					'',
					username:				'',
					password:				'',
					confirm:				'',
					question:				'',
					customQuestion:	'',
					answer:					''
				},
				message: {
					status:			'',
					message:		''
				},
				schools:			[],
				gradelevels:	[],
			};
		},
		computed: {
			...mapGetters([ 'students', 'profileLocale' ]),
			isDisabled() {
				let isCustomFilled = true;

				if(this.student.question === 'custom') {
					isCustomFilled = !!this.student.customQuestion;
				}

				return !!this.student.email &&
							 !!this.student.username &&
							 !!this.student.password &&
							 !!this.student.question &&
							 !!this.student.answer &&
							 isCustomFilled &&
							 this.student.answer.length > 3 &&
							 this.student.password === this.student.confirm;
			},
			invaildStudentAccessRequest() {
				let invalidData = false;

				if(this.requestStudents.length > 0) {
					for(let r in this.requestStudents) {
						let student = this.requestStudents[r];

						if(!student.firstName || !student.lastName || !student.birthday || !student.grade || !student.school) {
							invalidData = true;
						}
					}
				}

				return invalidData;
			}
		},
		components: {
			ValidationProvider,
			FontAwesomeIcon,
			DatePicker
		},
		methods: {
			/**
			* Add student method
			*/
			addStudent() {
				this.requestStudents.push(
					{
						firstName:	'',
						lastName:		'',
						birthday:		'',
						grade:			0,
						school:			null
					}
				);
			},
			/**
			* Remove student method
			*/
			removeStudent( index ) {
				this.requestStudents.splice( index, 1 );
			},
			getLocaleCode() {
				let code = 'en';

				if( !!this.profileLocale ) {
					if( !!this.profileLocale.LOCALE_CODE ) {
						code = this.profileLocale.LOCALE_CODE === 'en-sap' ?
							'en' : this.profileLocale.LOCALE_CODE;
					}
				}

				return code;
			},
			requestAccess() {
				const self = this;

				let schoolList = new Schools({});
				let grades = new Gradelevels();

				const schoolListPromise = schoolList.get();
				const gradelevelPromise = grades.get();

				schoolListPromise.then(response => {
					self.$set(self, 'schools', schoolList.getResponseData());
					self.isRequestAccess = true;
				});

				gradelevelPromise.then(response => {
					self.$set(self, 'gradelevels', grades.getResponseData());
					self.isRequestAccess = true;
				});
			},
			/**
			* Submits a request for new students
			*/
			requestNewStudents() {
				const self = this;

				let student =  new Students({
					data: self.getRequestedStudentData()
				});

				const promise = student.post();

				promise.then(response => {
					self.message.message = student.getResponseData().MESSAGE;

					if(!(student.getResponseData().ERROR)) {
						self.message.status = 'success';
						self.cancel();
					} else {
						self.message.status = 'danger';
					}
				});
			},
			getRequestedStudentData() {
				let studentsList = [];

				for(let s in this.requestStudents) {
					let student = this.requestStudents[s];

					studentsList.push({
						first_name:		student.firstName,
						last_name:		student.lastName,
						birth_date:		moment(student.birthday).format('MM/DD/YYYY'),
						grade_level:	student.grade,
						school_id:		student.school
					});
				}

				return studentsList;
			},
			cancel() {
				this.$set(this, 'student', {
					email:					'',
					username:				'',
					password:				'',
					confirm:				'',
					question:				'',
					customQuestion:	'',
					answer:					''
				});

				this.$set(this, 'requestStudents', [
					{
						firstName:	'',
						lastName:		'',
						birthday:		'',
						grade:			0,
						school:			null
					}
				]);

				this.isRequestAccess = false;
				this.isCreatingAccount = false;
			},
			extendDateValidation() {
				Validator.extend( "is_moment_date", {
					getMessage: (field) => `The ${field} field must be a valid date.`,
					validate: ( value ) =>
						// Check if the value is an instance of Date and is valid
						value instanceof Date && !isNaN(value)
				} );
			}
		},
		created() {
			this.$store.commit('setTitleIcon', 'users');
			this.$store.commit('setTitle', this.$t('MAIN_CONTENT.Students.students'));
			this.$store.commit('setUseHeader', true);

			this.extendDateValidation();
		}
	}
</script>
